import LegalPage, { ContactLink, LegalSection, Term } from './legal/LegalPage';

/**
 * Rewritten 2026-10-27 for Find (live location, the location track, Bluetooth) and in-app account deletion; the
 * August version said Prii collected no precise location. The retention numbers are the live *_retention_days()
 * functions - change them here when those change. A draft until someone qualified has read it.
 */
export default function PrivacyPolicy() {
  return (
    <LegalPage title="Prii Privacy Policy" updated="October 27, 2026">
      <p>
        Prii (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) helps you find live music,
        coordinate a group, and find your friends at the event. This policy explains what we
        collect, how we use it, who sees it, and your choices.
      </p>

      <LegalSection title="Information We Collect">
        <p>
          <Term>Account information:</Term> your email address and password (handled by our
          authentication provider; we never see your password), and the display name, first and last
          name, profile photo, Instagram handle and ZIP code you choose to add.
        </p>
        <p>
          <Term>Social content:</Term> who you follow, the groups you join, messages you send in
          group and direct chats, events you RSVP to, and the sets you pick.
        </p>
        <p>
          <Term>Precise location:</Term> when you share your location for an event, Prii uses your
          phone&apos;s GPS to show you and your group on the event map and in Find. This only runs
          around an event you are going to: from 3 hours before it starts to 3 hours after it ends,
          and near the venue. It stops outside that window, and you can stop sharing at any time.
          While it runs, your phone keeps a track of your positions (also when you are offline) and
          uploads it so your group sees where you are.
        </p>
        <p>
          <Term>Bluetooth:</Term> during an event, Prii uses Bluetooth to tell when your
          friends&apos; phones are nearby, which works where GPS and signal do not. Your phone sends
          a short identifier that changes regularly and means nothing outside Prii.
        </p>
        <p>
          <Term>Compass and motion:</Term> used on your phone to point you toward a friend. This
          stays on your phone.
        </p>
        <p>
          <Term>Camera and photos:</Term> only when you choose to add a photo, and only for that
          photo.
        </p>
        <p>
          <Term>Diagnostics:</Term> app logs (errors, the app version, how Find performed) so we can
          fix problems.
        </p>
      </LegalSection>

      <LegalSection title="How We Use Your Information">
        <ul className="list-disc pl-5 space-y-2">
          <li>
            To run Prii: your account, profile, groups, chat, RSVPs, set picks, the event map and
            Find
          </li>
          <li>To show your location to the people in your group for that event</li>
          <li>To suggest friends and groups you may want to join</li>
          <li>To contact you about your account or changes to Prii</li>
          <li>To keep Prii secure and reliable</li>
        </ul>
        <p>We do not show ads, and we do not sell or rent your personal information.</p>
      </LegalSection>

      <LegalSection title="Who Sees What">
        <ul className="list-disc pl-5 space-y-2">
          <li>Your profile (name, photo, Instagram handle) is visible to other Prii users.</li>
          <li>
            Events you are going to and your set picks are visible to people who can see your
            profile and your groups.
          </li>
          <li>
            Your live location is visible only to members of your group for that event, and only
            while you share it.
          </li>
          <li>Messages are visible to the people in that chat.</li>
        </ul>
      </LegalSection>

      <LegalSection title="Service Providers">
        <p>
          Prii uses these providers to operate. They process data for us under their own security
          practices.
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <Term>Supabase</Term> — authentication, database and file storage
          </li>
          <li>
            <Term>Vercel</Term> — web hosting
          </li>
          <li>
            <Term>Spotify</Term> — artist previews you choose to play; Spotify&apos;s own terms and
            privacy policy apply to the player
          </li>
          <li>
            <Term>Ticketmaster, AXS and festival websites</Term> — sources of event listings; we
            send them none of your personal data
          </li>
        </ul>
        <p>
          We may also disclose information when the law requires it, or to protect the safety of our
          users.
        </p>
      </LegalSection>

      <LegalSection title="How Long We Keep It">
        <p>
          Your account information and content are kept while your account is open. Location tracks
          are deleted after 30 days, other location history and Find logs after 14 days, and app
          logs after 7 days.
        </p>
      </LegalSection>

      <LegalSection title="Your Choices">
        <ul className="list-disc pl-5 space-y-2">
          <li>Edit your profile at any time.</li>
          <li>
            Stop sharing your location in Prii, or turn off Location or Bluetooth for Prii in your
            phone&apos;s settings.
          </li>
          <li>Turn notifications on or off in Settings.</li>
          <li>
            Delete your account in Settings &rarr; Delete Account. This permanently deletes your
            account and data.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          Prii is not for children under 13, and we do not knowingly collect information from
          children under 13. If you believe a child has given us information, contact us and we will
          delete it.
        </p>
      </LegalSection>

      <LegalSection title="Changes to This Policy">
        <p>
          We will update this policy as Prii changes, and update the date above when we do. If a
          change is significant, we will tell you in the app.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Questions about this policy or your data? Contact us at <ContactLink />.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
